import { useState, useEffect, useCallback, useRef } from 'react';
import campaignService from '../services/CampaignService';

const useCampaign = () => {
  // State management
  const [campaigns, setCampaigns] = useState([]);
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Utility functions
  const logInfo = (message, data = null) => {
    console.log(`[useCampaign] ${message}`, data || '');
  };

  const logError = (message, error = null) => {
    console.error(`[useCampaign] ERROR: ${message}`, error || '');
  };

  const setErrorState = (error) => {
    const errorMessage = error?.message || error || 'An unknown error occurred';
    setError(errorMessage);
    logError('Setting error state', errorMessage);
  };

  const clearError = () => {
    setError(null);
  };

  // Load campaigns list
  const loadCampaigns = useCallback(async (filters = {}) => {
    logInfo('Loading campaigns list', filters);
    setLoading(true);
    clearError();

    try {
      const campaignsList = await campaignService.listCampaigns(filters);
      setCampaigns(campaignsList);
      logInfo('Campaigns loaded successfully', { count: campaignsList.length });

      return campaignsList;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new campaign
  const createCampaign = useCallback(async (campaignData, options = {}) => {
    logInfo('Creating new campaign', { campaignData, options });
    setLoading(true);
    clearError();

    try {
      const newCampaign = await campaignService.createCampaign(campaignData, options);
      
      // Update campaigns list
      setCampaigns(prev => [...prev, newCampaign.campaign || newCampaign]);
      
      // Set as current campaign
      setCurrentCampaign(newCampaign.campaign || newCampaign);
      
      logInfo('Campaign created successfully', newCampaign);
      return newCampaign;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get campaign details
  const getCampaignDetails = useCallback(async (campaignId) => {
    logInfo('Getting campaign details', { campaignId });
    setLoading(true);
    clearError();

    try {
      const campaign = await campaignService.getCampaign(campaignId);
      logInfo('Campaign details loaded', campaign);
      return campaign;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update campaign
  const updateCampaign = useCallback(async (campaignId, updateData) => {
    logInfo('Updating campaign', { campaignId, updateData });
    setLoading(true);
    clearError();

    try {
      const updatedCampaign = await campaignService.updateCampaign(campaignId, updateData);
      
      // Update in campaigns list
      setCampaigns(prev => 
        prev.map(campaign => 
          campaign.id === campaignId ? { ...campaign, ...updatedCampaign } : campaign
        )
      );

      // Update current campaign if it's the one being updated
      if (currentCampaign && currentCampaign.id === campaignId) {
        setCurrentCampaign(prev => ({ ...prev, ...updatedCampaign }));
      }

      logInfo('Campaign updated successfully', updatedCampaign);
      return updatedCampaign;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [currentCampaign]);

  // Generate posts for campaign
  const generatePosts = useCallback(async (campaignId, generationOptions = {}) => {
    logInfo('Generating posts for campaign', { campaignId, generationOptions });
    setGenerating(true);
    clearError();

    try {
      const generationResult = await campaignService.generatePosts(campaignId, generationOptions);
      logInfo('Posts generated successfully', generationResult);
      return generationResult;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setGenerating(false);
    }
  }, []);

  // Delete campaign
  const deleteCampaign = useCallback(async (campaignId) => {
    logInfo('Deleting campaign', { campaignId });
    setLoading(true);
    clearError();

    try {
      await campaignService.deleteCampaign(campaignId);
      
      // Remove from campaigns list
      setCampaigns(prev => prev.filter(campaign => campaign.id !== campaignId));
      
      // Clear current campaign if it was deleted
      if (currentCampaign && currentCampaign.id === campaignId) {
        setCurrentCampaign(null);
      }

      logInfo('Campaign deleted successfully');
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [currentCampaign]);

  // Activate campaign
  const activateCampaign = useCallback(async (campaignId) => {
    logInfo('Activating campaign', { campaignId });
    setLoading(true);
    clearError();

    try {
      const activatedCampaign = await campaignService.activateCampaign(campaignId);
      
      // Update campaigns list with new status
      setCampaigns(prev => 
        prev.map(campaign => 
          campaign.id === campaignId ? { ...campaign, status: 'active' } : campaign
        )
      );

      logInfo('Campaign activated successfully', activatedCampaign);
      return activatedCampaign;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Pause campaign
  const pauseCampaign = useCallback(async (campaignId) => {
    logInfo('Pausing campaign', { campaignId });
    setLoading(true);
    clearError();

    try {
      const pausedCampaign = await campaignService.pauseCampaign(campaignId);
      
      // Update campaigns list with new status
      setCampaigns(prev => 
        prev.map(campaign => 
          campaign.id === campaignId ? { ...campaign, status: 'paused' } : campaign
        )
      );

      logInfo('Campaign paused successfully', pausedCampaign);
      return pausedCampaign;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Complete campaign
  const completeCampaign = useCallback(async (campaignId) => {
    logInfo('Completing campaign', { campaignId });
    setLoading(true);
    clearError();

    try {
      const completedCampaign = await campaignService.completeCampaign(campaignId);
      
      // Update campaigns list with new status
      setCampaigns(prev => 
        prev.map(campaign => 
          campaign.id === campaignId ? { ...campaign, status: 'completed' } : campaign
        )
      );

      logInfo('Campaign completed successfully', completedCampaign);
      return completedCampaign;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Duplicate campaign
  const duplicateCampaign = useCallback(async (campaignId, overrides = {}) => {
    logInfo('Duplicating campaign', { campaignId, overrides });
    setLoading(true);
    clearError();

    try {
      const duplicatedCampaign = await campaignService.duplicateCampaign(campaignId, overrides);
      
      // Add to campaigns list
      setCampaigns(prev => [...prev, duplicatedCampaign.campaign || duplicatedCampaign]);
      
      logInfo('Campaign duplicated successfully', duplicatedCampaign);
      return duplicatedCampaign;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get campaign analytics
  const getCampaignAnalytics = useCallback(async (campaignId, analyticsOptions = {}) => {
    logInfo('Getting campaign analytics', { campaignId, analyticsOptions });
    setLoading(true);
    clearError();

    try {
      const analytics = await campaignService.getCampaignAnalytics(campaignId, analyticsOptions);
      logInfo('Campaign analytics loaded', analytics);
      return analytics;
    } catch (error) {
      setErrorState(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Switch current campaign
  const switchCampaign = useCallback((campaign) => {
    logInfo('Switching current campaign', campaign);
    setCurrentCampaign(campaign);
  }, []);

  // Load campaigns on mount (using ref to prevent multiple loads)
  const hasLoadedRef = useRef(false);
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadCampaigns().catch(error => {
        logError('Failed to load campaigns on mount', error);
      });
    }
  }, []); // Empty array - run only once on mount

  return {
    // State
    campaigns,
    currentCampaign,
    loading,
    error,
    generating,

    // Actions
    loadCampaigns,
    createCampaign,
    getCampaignDetails,
    updateCampaign,
    generatePosts,
    deleteCampaign,
    activateCampaign,
    pauseCampaign,
    completeCampaign,
    duplicateCampaign,
    getCampaignAnalytics,
    switchCampaign,
    clearError,

    // Computed state
    hasCampaigns: campaigns.length > 0,
    hasCurrentCampaign: !!currentCampaign,
    isProcessing: loading || generating,

    // Campaign filtering helpers
    getDraftCampaigns: () => campaigns.filter(c => c.status === 'draft'),
    getActiveCampaigns: () => campaigns.filter(c => c.status === 'active'),
    getPausedCampaigns: () => campaigns.filter(c => c.status === 'paused'),
    getCompletedCampaigns: () => campaigns.filter(c => c.status === 'completed')
  };
};

export default useCampaign; 